import { MapPortalData } from "../data";
import { InteractablePolygon } from "./InteractablePolygon";
import React from "react";
import { PortalLabel } from "./PortalLabel";
import "./MapPortal.css";
import { MapPortalLinesType, useAppSelector } from "../state";

export interface MapPortalProps extends MapPortalData {
  offsetMapCoords: (x: number, y: number) => void;
  index: number;
  // Current map scale, passed as a ref rather than a value so that zooming
  // doesn't re-render every portal on every frame. The scale is only read
  // inside click handlers, which always see the latest value via the ref.
  scaleRef: React.RefObject<number>;
  color: string;
  size: number;
}

export const MapPortal = React.memo((props: MapPortalProps) => {
  const { portal1, portal2, scaleRef, offsetMapCoords, index, color, size } =
    props;

  const {
    showMapPortals: show,
    showMapPortalLines,
    showMapPortalLinesType,
  } = useAppSelector((state) => state.settings);

  const [hovered, setHovered] = React.useState(false);

  const showLine = React.useMemo(() => {
    if (showMapPortalLines) {
      if (showMapPortalLinesType === MapPortalLinesType.Hover) {
        return hovered;
      }
      return true;
    }
    return false;
  }, [showMapPortalLines, showMapPortalLinesType, hovered]);

  // Notes:
  // Positive x means we have to move map to the right (and vice versa)
  // Negative y means we have to move map up (and vice versa)
  const onPortal1Click = React.useCallback(() => {
    // Move map to portal2
    // Calculate distance between the two
    const scale = scaleRef.current ?? 1;
    const xDiff = portal1.x - portal2.x;
    const yDiff = portal1.y - portal2.y;
    offsetMapCoords(xDiff * scale, yDiff * scale);
  }, [portal1, portal2, offsetMapCoords, scaleRef]);

  const onPortal2Click = React.useCallback(() => {
    // Move map to portal1
    // Calculate distance between the two
    const scale = scaleRef.current ?? 1;
    const xDiff = portal2.x - portal1.x;
    const yDiff = portal2.y - portal1.y;
    offsetMapCoords(xDiff * scale, yDiff * scale);
  }, [portal1, portal2, offsetMapCoords, scaleRef]);

  return (
    <>
      <InteractablePolygon
        onClick={onPortal1Click}
        onMouseEnter={() => setHovered(true)}
        onMouseLeave={() => setHovered(false)}
        x={portal1.x}
        y={portal1.y}
        height={size}
        width={size}
      ></InteractablePolygon>
      <InteractablePolygon
        onClick={onPortal2Click}
        onMouseEnter={() => setHovered(true)}
        onMouseLeave={() => setHovered(false)}
        x={portal2.x}
        y={portal2.y}
        height={size}
        width={size}
      ></InteractablePolygon>
      {show && (
        <>
          <PortalLabel
            x={portal1.x}
            y={portal1.y}
            show={false}
            portalIndex={index}
            color={color}
            hovered={hovered}
          />
          <PortalLabel
            x={portal2.x}
            y={portal2.y}
            show={false}
            portalIndex={index}
            color={color}
            hovered={hovered}
          />
        </>
      )}
      {showLine && (
        <line
          className={`map-portal-line ${hovered ? "hovered" : ""}`}
          x1={portal1.x + 10}
          y1={portal1.y + 10}
          x2={portal2.x + 10}
          y2={portal2.y + 10}
          stroke={color}
          onMouseEnter={() => setHovered(true)}
          onMouseLeave={() => setHovered(false)}
        />
      )}
    </>
  );
});
